import { Badge } from '@app/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { formatDate, formatPairs, type DashboardRecentEmir, type DashboardRecentOrder } from './api'
import { Link } from '@tanstack/react-router'
import { cn } from '@app/lib/cn'

const rowLinkClass = cn(
  'font-mono font-medium text-kordovan underline-offset-2 hover:underline',
)

/**
 * Recent orders and recent production emirs.
 *
 * Codes arrive resolved to human names (customer from `Cari_Kart`, model from
 * `Model_M`) — Korgun's number-one complaint was reading bare codes. The number
 * cell is the row link into the detail screen; no record arrows.
 */

const ORDER_STATUS = {
  overdue: { tone: 'warn', label: 'Gecikti' },
  open: { tone: 'info', label: 'Açık' },
  closed: { tone: 'ok', label: 'Kapandı' },
} as const

const EMIR_STATUS_TONE: Record<string, 'ok' | 'warn' | 'info' | 'neutral'> = {
  X: 'ok',
  B: 'info',
  '': 'neutral',
}

export function RecentTables({
  orders,
  emirs,
}: {
  orders: DashboardRecentOrder[]
  emirs: DashboardRecentEmir[]
}) {
  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader>
          <div>
            <CardTitle>Son siparişler</CardTitle>
            <CardDescription>Müşteri siparişleri, en yenisi üstte.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {orders.length === 0 ? (
            <EmptyState
              title="Henüz sipariş kaydı yok"
              description="Korgun'dan sipariş aktarıldığında burada listelenir."
            />
          ) : (
            <TableScroller>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Sipariş</TableHead>
                    <TableHead>Müşteri</TableHead>
                    <TableHead className="w-24">Teslim</TableHead>
                    <TableHead className="w-20 text-right">Kalan</TableHead>
                    <TableHead className="w-24">Durum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const status = ORDER_STATUS[order.status]
                    return (
                      <TableRow key={order.sipNo}>
                        <TableCell>
                          <Link
                            to="/siparisler/$sipNo"
                            params={{ sipNo: String(order.sipNo) }}
                            title={`${order.sipNo} numaralı siparişi aç`}
                            className={rowLinkClass}
                          >
                            {order.sipNo}
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-0">
                          <span className="block truncate text-ink" title={order.customer}>
                            {order.customer}
                          </span>
                          {order.documentNo ? (
                            <span className="block truncate font-mono text-micro text-ink-muted">
                              {order.documentNo}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-ink-muted">
                          {formatDate(order.deliveryDate)}
                        </TableCell>
                        <TableNumCell className="font-medium">
                          {formatPairs(order.kalan)}
                        </TableNumCell>
                        <TableCell>
                          <Badge tone={status.tone}>{status.label}</Badge>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableScroller>
          )}
        </CardContent>
      </Card>

      <Card className="min-w-0">
        <CardHeader>
          <div>
            <CardTitle>Son üretim emirleri</CardTitle>
            <CardDescription>Model adıyla, en yenisi üstte.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {emirs.length === 0 ? (
            <EmptyState
              title="Henüz üretim emri yok"
              description="Emir açıldığında model adı ve adediyle burada görünür."
            />
          ) : (
            <TableScroller>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Emir</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead className="w-24">Tarih</TableHead>
                    <TableHead className="w-20 text-right">Adet</TableHead>
                    <TableHead className="w-28">Durum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emirs.map((emir) => (
                    <TableRow key={emir.emirNo}>
                      <TableCell>
                        <Link
                          to="/uretim/$emirNo"
                          params={{ emirNo: String(emir.emirNo) }}
                          title={`${emir.emirNo} numaralı üretim emrini aç`}
                          className={rowLinkClass}
                        >
                          {emir.emirNo}
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-0">
                        <span className="block truncate text-ink" title={emir.model}>
                          {emir.model}
                        </span>
                        {emir.modelKod ? (
                          <span className="block truncate font-mono text-micro text-ink-muted">
                            {emir.modelKod}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono text-ink-muted">
                        {formatDate(emir.date)}
                      </TableCell>
                      <TableNumCell className="font-medium">
                        {formatPairs(emir.quantity)}
                      </TableNumCell>
                      <TableCell>
                        <Badge tone={EMIR_STATUS_TONE[emir.statusCode] ?? 'neutral'}>
                          {emir.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScroller>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="px-3.5 py-8 text-center">
      <p className="text-data font-medium text-ink">{title}</p>
      <p className="mt-0.5 text-micro text-ink-muted">{description}</p>
    </div>
  )
}
